import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import client from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import DifficultyBadge from "../components/DifficultyBadge";
import { ChallengeModal } from "./ChallengeModal";

interface HintPublic {
  id: string;
  level: number;
  content: string;
  cost: number;
  unlocked: boolean;
}

interface ChallengePublic {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: "EASY" | "MEDIUM" | "HARD" | "INSANE";
  basePoints: number;
  currentPoints: number;
  tags: string[];
  hasDockerInstance: boolean;
  isActive: boolean;
  solveCount: number;
  firstBloodUser: any;
  firstBloodAt: string | null;
  prerequisiteId: string | null;
  prerequisiteMet: boolean;
  solved: boolean;
  hints: HintPublic[];
}

const CATEGORIES = [
  { value: "ALL" as const, label: "All" },
  { value: "Web" as const, label: "Web" },
  { value: "Crypto" as const, label: "Crypto" },
  { value: "Forensics" as const, label: "Forensics" },
  { value: "Pwn" as const, label: "Pwn" },
  { value: "Reversing" as const, label: "Reversing" },
  { value: "Misc" as const, label: "Misc" },
  { value: "OSINT" as const, label: "OSINT" },
  { value: "Hardware" as const, label: "Hardware" },
  { value: "Blockchain" as const, label: "Blockchain" },
  { value: "Stego" as const, label: "Stego" },
];
type CategoryFilter = typeof CATEGORIES[number]["value"];
type DifficultyFilter = "ALL" | "EASY" | "MEDIUM" | "HARD" | "INSANE";


const difficulties: DifficultyFilter[] = ["ALL", "EASY", "MEDIUM", "HARD", "INSANE"];

export default function Challenges() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [challenges, setChallenges] = useState<ChallengePublic[]>([]);
  const [selectedChallenge, setSelectedChallenge] = useState<ChallengePublic | null>(null);
  const [category, setCategory] = useState<CategoryFilter>("ALL");
  const [difficulty, setDifficulty] = useState<DifficultyFilter>("ALL");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchChallenges = useCallback(async (pageNum: number, append = false, signal?: AbortSignal) => {
    try {
      const params = new URLSearchParams();
      if (category !== "ALL") params.append("category", category);
      if (difficulty !== "ALL") params.append("difficulty", difficulty);
      if (search) params.append("search", search);
      params.append("page", pageNum.toString());
      params.append("limit", "12");

      const response = await client.get(`/challenges?${params.toString()}`, { signal });
      const result = response.data?.data || response.data;
      const data = Array.isArray(result?.data) ? result : { data: result || [], pagination: { totalPages: 1 } };
      const challengesList = data.data || [];
      const totalPages = data.pagination?.totalPages || 1;

      if (append) {
        setChallenges((prev) => [...prev, ...challengesList]);
      } else {
        setChallenges(challengesList);
      }
      setHasMore(pageNum < totalPages);
    } catch (error) {
      toast({
        title: "Failed to load challenges",
        variant: "error",
      });
    }
  }, [category, difficulty, search, toast]);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setPage(1);
    fetchChallenges(1, false, controller.signal).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [category, difficulty, search, fetchChallenges]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    await fetchChallenges(nextPage, true);
    setPage(nextPage);
    setLoadingMore(false);
  };

  const handleChallengeClick = (challenge: ChallengePublic) => {
    setSelectedChallenge(challenge);
  };

  const handleCloseModal = () => {
    setSelectedChallenge(null);
  };

  const handleChallengeUpdate = (updated: ChallengePublic) => {
    setChallenges((prev) =>
      prev.map((c) => (c.id === updated.id ? updated : c))
    );
    setSelectedChallenge(updated);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  return (
    <div className="min-h-screen bg-ds-bg p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="relative">
              <div className="absolute inset-0 bg-ds-accent/20 blur-xl rounded-full" />
              <h1 className="text-3xl font-bold text-ds-text relative z-10">
                Challenge Arena
              </h1>
            </div>
            {/* Radar decoration */}
            <div className="relative w-12 h-12 flex items-center justify-center">
              <div className="absolute inset-0 border border-ds-accent/30 rounded-full animate-ping opacity-20" />
              <div className="absolute inset-1 border border-ds-accent/40 rounded-full" />
              <div className="absolute inset-2 border border-ds-accent/50 rounded-full" />
              <div className="w-2 h-2 bg-ds-accent rounded-full" />
            </div>
            {(user?.role === "SUPER_ADMIN" || user?.role === "ORGANIZER" || user?.role === "AUTHOR") && (
              <Link
                to="/admin"
                className="ml-auto ds-btn-primary text-sm"
              >
                + Add Challenge
              </Link>
            )}
          </div>
          <p className="text-ds-text-muted">Select a challenge and prove your skills</p>
        </div>

        {/* Filter Bar */}
        <div className="bg-ds-surface border border-ds-border rounded-xl p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setCategory(value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    category === value
                      ? "bg-ds-accent text-white shadow-lg shadow-ds-accent/25"
                      : "bg-ds-bg text-ds-text-muted hover:text-ds-text hover:bg-ds-border"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex gap-4 flex-1">
              {/* Difficulty Filter */}
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as DifficultyFilter)}
                className="bg-ds-bg border border-ds-border rounded-lg px-4 py-2 text-ds-text text-sm focus:outline-none focus:border-ds-accent transition-colors"
              >
                {difficulties.map((diff) => (
                  <option key={diff} value={diff}>
                    {diff === "ALL" ? "All Difficulties" : diff}
                  </option>
                ))}
              </select>

              {/* Search Input */}
              <input
                type="text"
                placeholder="Search challenges..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-ds-bg border border-ds-border rounded-lg px-4 py-2 text-ds-text placeholder-ds-text-muted focus:outline-none focus:border-ds-accent transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Challenges Grid */}
        {loading ? (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="break-inside-avoid bg-ds-surface border border-ds-border rounded-xl p-5 animate-pulse"
              >
                <div className="h-4 bg-ds-border rounded w-16 mb-3" />
                <div className="h-6 bg-ds-border rounded w-3/4 mb-3" />
                <div className="h-4 bg-ds-border rounded w-20 mb-4" />
                <div className="flex gap-2 mb-4">
                  <div className="h-6 bg-ds-border rounded-full w-16" />
                  <div className="h-6 bg-ds-border rounded-full w-12" />
                </div>
                <div className="h-8 bg-ds-border rounded" />
              </div>
            ))}
          </div>
        ) : challenges.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative w-24 h-24 mb-6">
              <div className="absolute inset-0 border-2 border-ds-border rounded-full" />
              <div className="absolute inset-2 border-2 border-ds-border rounded-full opacity-60" />
              <div className="absolute inset-4 border-2 border-ds-border rounded-full opacity-30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-3 h-3 bg-ds-border rounded-full" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-ds-text mb-2">No challenges found</h3>
            <p className="text-ds-text-muted">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <motion.div
            className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            <AnimatePresence mode="popLayout">
              {challenges.map((challenge) => (
                <motion.div
                  key={challenge.id}
                  variants={cardVariants}
                  layout
                  className="break-inside-avoid"
                >
                  <button
                    onClick={() => handleChallengeClick(challenge)}
                    className="w-full text-left bg-ds-surface border border-ds-border rounded-xl p-5 hover:border-ds-accent/50 hover:shadow-lg hover:shadow-ds-accent/5 transition-all duration-200 group"
                  >
                    {/* Header: Category + Difficulty */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-mono text-ds-text-muted uppercase tracking-wider">
                        {challenge.category}
                      </span>
                      <DifficultyBadge difficulty={challenge.difficulty} />
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-ds-text mb-2 group-hover:text-ds-accent transition-colors">
                      {challenge.title}
                    </h3>

                    {/* Points */}
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-xl font-mono font-bold text-ds-accent">
                        {challenge.currentPoints}
                      </span>
                      <span className="text-sm text-ds-text-muted">pts</span>
                      {challenge.basePoints !== challenge.currentPoints && (
                        <span className="text-sm font-mono text-ds-text-muted line-through">
                          {challenge.basePoints}
                        </span>
                      )}
                    </div>

                    {/* Tags */}
                    {challenge.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {challenge.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-ds-bg text-ds-text-muted text-xs rounded-md border border-ds-border"
                          >
                            {tag}
                          </span>
                        ))}
                        {challenge.tags.length > 3 && (
                          <span className="px-2 py-0.5 text-ds-text-muted text-xs">
                            +{challenge.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer: Solve Count + Status */}
                    <div className="flex items-center justify-between pt-3 border-t border-ds-border">
                      <span className="text-sm text-ds-text-muted font-mono">
                        {challenge.solveCount} solve{challenge.solveCount !== 1 ? "s" : ""}
                      </span>
                      <div className="flex items-center gap-2">
                        {challenge.firstBloodUser && (
                          <span className="text-xs px-2 py-0.5 bg-red-500/10 text-red-400 rounded font-medium">
                            First Blood
                          </span>
                        )}
                        {challenge.solved && (
                          <span className="flex items-center gap-1 text-xs text-ds-success">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Solved
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Load More */}
        {!loading && challenges.length > 0 && hasMore && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-8 py-3 bg-ds-surface border border-ds-border rounded-xl text-ds-text font-medium hover:border-ds-accent/50 hover:bg-ds-accent/10 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading...
                </span>
              ) : (
                "Load More Challenges"
              )}
            </button>
          </div>
        )}

        {/* Challenge Modal */}
        {selectedChallenge && (
          <ChallengeModal
            challenge={selectedChallenge}
            isOpen={true}
            onClose={handleCloseModal}
            onUpdate={handleChallengeUpdate}
          />
        )}
      </div>
    </div>
  );
}